import React, { useEffect, useMemo } from 'react';

import config from 'config';
import PropTypes from 'prop-types';

import { Script } from '../../../../techstyle-shared/react-marketing';
import { useDomain } from '../../../../techstyle-shared/redux-core';

const widgetBtnStyle = {
  scale: [0.5, 0.5],
  vPosition: ['90%', '90%'],
  color: {
    main: '#1C4BB6',
    second: '#FFFFFF',
  },
  icon: { type: 11, shape: 'circle' },
};

// make the widget so small that it is effectively invisible
const widgetBtnStyleTINY = {
  scale: [0.001, 0.001],
  vPosition: ['90%', '90%'],
  color: {
    main: '#1C4BB6',
    second: '#FFFFFF',
  },
  icon: { type: 11, shape: 'circle' },
};

export function EqualWebSnippet({
  position = 'right',
  btnStyle = widgetBtnStyle,
  isFooter = false, // when in footer, floating widget button is removed. This is set if user is logged in
  ...rest
}) {
  // must use tldLocale to handle EU domain for SXF. Config is not set when this
  // is mounted so we can not import tldLocale directly, attempting to do so
  // breaks the build. Porting over the logic here instead.
  const { tld } = useDomain();
  const builderLocales = config.get('public.intl.builderLocales');
  const locale = builderLocales[tld];
  const menuLang = useMemo(() => locale.split('-')[0].toUpperCase(), [locale]);
  const equalWebSitekeys = config.get('public.equalWeb.siteKeys');
  const equalWebSitekey = equalWebSitekeys[locale];

  useEffect(() => {
    if (!window.interdeal) {
      // sets interdeal if not already set
      window.interdeal = {
        sitekey: equalWebSitekey,
        Position: position,
        draggable: false,
        domains: {
          js: 'https://aacdn.nagich.com/',
          acc: 'https://access.nagich.com/',
        },
      };
    }
    // update widget config if locale is updated
    window.interdeal.Menulang = menuLang;

    // or if user logs in (footer button is used instead of floating widget)
    //
    // this does not get applied until after the page is refreshed. In most instances
    // this would not be necessary as AccesibilitySnippet removes the floating menu
    // button, but we found rare instances in Ghost Inspector where the button
    // was not removed.
    window.interdeal.hideBtn = isFooter;
    // not really necessary given the above, but might as well just in case.
    window.interdeal.btnStyle = isFooter ? widgetBtnStyleTINY : widgetBtnStyle;
  }, [menuLang, position, equalWebSitekey, btnStyle, isFooter]);

  return (
    <Script
      async
      crossOrigin="anonymous"
      data-cfasync="true"
      globalScriptKey="ada-widget"
      integrity="sha512-PUyQFF3HFjRiVfjOiFFu+RTc0nGmLV5FN3CVw8zWFK6pVbWPAEKy9X2bTUn10GNu1EbxN56MuWu0P8ZHC6xv3Q=="
      skipServerRender
      src="https://aacdn.nagich.com/core/5.1.2/accessibility.js"
      defer="true"
      {...rest}
    />
  );
}

EqualWebSnippet.propTypes = {
  /**
   * Button style for the widget menu.
   */
  btnStyle: PropTypes.object,
  /**
   * If true, the widget is rendered in the footer.
   */
  isFooter: PropTypes.bool,
  /**
   * Position for widget menu.
   */
  position: PropTypes.oneOf(['left', 'right']),
};

export default React.memo(EqualWebSnippet);
